import { Directive, HostAttributeToken, inject, input } from '@angular/core';
import { KeyCode } from '../../domain/key-code.enum';
import { HelpLinkService } from './help-link.service';

/**
 * The Help Link Directive
 *
 * Can be applied to link or a button to open help window
 */
@Directive({
  selector: '[tdWbHelpLink]',
  exportAs: 'tdWbHelpLink',
  host: {
    '[attr.href]': 'hrefAttr',
    '[attr.role]': 'roleAttr',
    '[attr.tabindex]': 'tabindexAttr',
    '(click)': 'launchHelpWindow($event)',
    '(keydown)': 'onKeyDown($event, $event.key)'
  }
})
export class HelpLinkDirective {
  private readonly role = inject(new HostAttributeToken('role'), { optional: true });
  private readonly helpLinkService = inject<HelpLinkService>(HelpLinkService);

  /**
   * The article id to be scrolled to in Help window
   */
  readonly articleId = input<string>(undefined, { alias: 'tdWbHelpLink' });

  hrefAttr: string;
  roleAttr: string;
  tabindexAttr: string;

  constructor() {
    const role = this.role;
    const tabIndex = inject(new HostAttributeToken('tabindex'), { optional: true });

    this.hrefAttr = '';
    this.roleAttr = role !== null ? role : 'button';
    this.tabindexAttr = tabIndex !== null ? tabIndex : '0';
  }

  launchHelpWindow(event?: MouseEvent | KeyboardEvent): void {
    if (event !== undefined) {
      event.preventDefault();
    }
    this.helpLinkService.open(this.getArticleId());
  }

  // TODO: use a common base class to open window, href and this directive

  onKeyDown(event: KeyboardEvent, keyCode: string): void {
    if ((this.role === 'button' && (keyCode === KeyCode.ENTER || keyCode === KeyCode.SPACE)) ||
      (this.role === 'link' && keyCode === KeyCode.ENTER)) {
      this.launchHelpWindow(event);
    }
  }

  close(): void {
    this.helpLinkService.close();
  }

  /**
   * This getter is used for getting the help article id. If not specified as input, returns helpId from current route's data
   *
   * @returns Returns id passed to help link service.
   */
  getArticleId(): string {
    const articleId = this.articleId();
    return articleId !== undefined && articleId.trim().length > 0
      ? articleId
      : this.helpLinkService.getHelpArticleId();
  }
}
